import { PermissionKey, PermissionScreen } from '@clean-code/types/permissions';

type LoginRoleRule = (role: string | undefined) => boolean;

// The login-role checks the app made before permissions. They are not consistent with each other
// (readonly counts as an admin, superuser only sometimes as a superadmin), and are kept exactly as
// they were for companies without the `rolePermissions` flag.
const everyone: LoginRoleRule = () => true;
const notUser: LoginRoleRule = (role) => role !== 'user';
const superAdminOrSuperUser: LoginRoleRule = (role) =>
  role === 'superadmin' || role === 'superuser';
const onlySuperAdmin: LoginRoleRule = (role) => role === 'superadmin';
const superAdminOrAdmin: LoginRoleRule = (role) =>
  role === 'superadmin' || role === 'admin';

/**
 * What each key answers from the login role. `everyone` marks controls that had no check of their
 * own: the screen they sit on was the only gate.
 */
const LOGIN_ROLE_RULES: Record<PermissionKey, LoginRoleRule> = {
  'member.read': onlySuperAdmin,
  'member.invite': superAdminOrSuperUser,
  'member.update': superAdminOrSuperUser,
  'member.remove': superAdminOrSuperUser,
  'role.read': superAdminOrSuperUser,

  'machine_group.read': notUser,
  'machine_group.manage': everyone,
  'machine.external_name.update': notUser,
  'machine.production_settings.update': superAdminOrSuperUser,
  'machine.view_config.company_default.update': superAdminOrSuperUser,

  'shift.create': everyone,
  'shift.update': everyone,
  'shift.delete': everyone,
  'shift.exclusion.manage': notUser,
  'config.shift_settings.update': everyone,
  'goal.create': notUser,

  'stop.cause.manage': notUser,
  'stop.loss_tree.manage': notUser,
  'stop.component_group.manage': notUser,
  'stop.automation.manage': notUser,
  'stop.classification.create': everyone,
  'stop.classification.update': everyone,

  'operator.create': everyone,
  'operator.update': everyone,
  'operator.delete': everyone,
  'operator_plan.create': notUser,
  'operator_plan.update': notUser,
  'operator_plan.delete': notUser,

  'alert.group.create': notUser,
  'alert.group.update': notUser,
  'alert.group.delete': notUser,
  'alert.setting.create': notUser,
  'alert.setting.update': everyone,
  'alert.setting.delete': notUser,
  'alert.persona.create': everyone,
  'alert.persona.update': everyone,
  'alert.persona.delete': everyone,
  'alert.persona.legacy.manage': onlySuperAdmin,
  'alert.persona.push.enable': everyone,
  'notification.read_any': superAdminOrSuperUser,

  'sku.create': notUser,
  'sku.update': everyone,
  'sku.archive': everyone,
  'sku.delete': everyone,
  'sku.select_all': superAdminOrSuperUser,
  'nominal_speed.create': notUser,
  'nominal_speed.update': notUser,
  'nominal_speed.delete': notUser,
  'quality_issue.create': notUser,
  'quality_issue.update': notUser,
  'quality_issue.delete': notUser,
  'raw_material.create': notUser,
  'raw_material.update': notUser,
  'raw_material.delete': notUser,

  'work_order.update': everyone,
  'work_order.delete': everyone,
  'work_order.legacy_table.manage': superAdminOrAdmin,
  'work_step.create': everyone,
  'work_step.clone': everyone,
  'work_step.update': superAdminOrAdmin,
  'work_step.delete': superAdminOrAdmin,
  'work_step.backdate': notUser,
  'production_advancement.nominal_speed.override': notUser,

  'company_configuration.update': everyone,
  'company_configuration.equipment_label.update': onlySuperAdmin,

  'api_key.read': superAdminOrSuperUser,
  'api_key.create': superAdminOrSuperUser,
  'api_key.delete': superAdminOrSuperUser
};

export const isGrantedByLoginRole = (
  key: PermissionKey,
  role: string | undefined
): boolean => LOGIN_ROLE_RULES[key](role);

const SUPERADMIN_SCREENS: PermissionScreen[] = [
  'usersSettings',
  'integrations'
];

/** Users settings and integrations are for superadmins; every other settings screen for anyone but `user`. */
export const canOpenByLoginRole = (
  screen: PermissionScreen,
  role: string | undefined
): boolean =>
  SUPERADMIN_SCREENS.includes(screen)
    ? superAdminOrSuperUser(role)
    : notUser(role);
